import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { BRAND, type Section } from "@neus/shared";
import { popularProductsQuery, useFacets, useProducts } from "../../api/hooks";
import { shown } from "../../content";
import { directionsUrl, mapEmbedUrl, mapsEmbedKey } from "../../maps";
import { isSnapshot } from "../../prerenderData";
import { STORE_HOURS } from "../../nav";
import { DepartmentTiles } from "../../pages/Directory";
import { BadgeIcon, ClockIcon } from "../Icons";
import { ProductGrid, ProductGridSkeleton } from "../product/ProductCard";
import { IconCard } from "../ui/IconCard";
import styles from "./Content.module.css";
import { ContentLinkView, Paragraphs, RichText } from "./RichText";

/*
 * The component library for brand content: one component per section type in
 * shared/src/contentSchema.ts, each taking that section's fields as props.
 */

type Props<T extends Section["type"]> = Extract<Section, { type: T }>;

/** A full-width band with the usual vertical padding; `prose` keeps text to a readable width. */
function Band({ alt, prose, children }: { alt: boolean; prose?: boolean; children: ReactNode }) {
  return (
    <section className={`section ${alt ? "section--alt" : ""} ${prose ? styles.proseBand : ""}`}>
      <div className={`container ${prose ? styles.prose : ""}`}>{children}</div>
    </section>
  );
}

function SectionTitle({ title, subtitle }: { title?: string; subtitle?: string }) {
  if (!title) return null;
  return (
    <div className="section-title">
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
  );
}

export function Hero({ kicker, title, text, actions }: Props<"hero">) {
  return (
    <section className={styles.hero}>
      <div className={`container ${styles.heroInner}`}>
        {kicker && <p className={styles.heroKicker}>{kicker}</p>}
        <h1>
          {title.map((line, i) => (
            <Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </Fragment>
          ))}
        </h1>
        {text && (
          <p className={styles.heroText}>
            <RichText text={text} />
          </p>
        )}
        {actions.length > 0 && (
          <div className={styles.heroCtas}>
            {actions.map((a) => (
              <ContentLinkView
                key={a.label}
                link={a}
                className={
                  a.style === "ghost" ? `btn btn--lg ${styles.heroGhost}` : a.style ? "btn btn--lg" : undefined
                }
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function PageHeader({ title, breadcrumbs }: Props<"pageHeader">) {
  return (
    <div className="page-header">
      <div className="container">
        {breadcrumbs && (
          <div className="breadcrumbs">
            <Link to="/">Home</Link> / {title}
          </div>
        )}
        <h1>{title}</h1>
      </div>
    </div>
  );
}

export function Stripe({ tone, title, text, action }: Props<"stripe">) {
  return (
    <section className={`${styles.stripe} ${styles[tone]}`}>
      <div className={`container ${styles.stripeInner}`}>
        <div>
          {title && <h2>{title}</h2>}
          {text && (
            <p>
              <RichText text={text} />
            </p>
          )}
        </div>
        {action && (
          <ContentLinkView
            link={action}
            // On a colored band, a button is outlined in the band's text color.
            className={action.style && tone !== "light" ? `btn ${styles.onColor}` : undefined}
          />
        )}
      </div>
    </section>
  );
}

export function Features({ title, tone, items, alt }: Props<"features">) {
  const grid = (
    <div className={styles.featureGrid}>
      {items.map((w) => (
        <div key={w.title}>
          <h3>{w.title}</h3>
          <p>
            <RichText text={w.text} />
          </p>
        </div>
      ))}
    </div>
  );
  if (tone === "light")
    return (
      <Band alt={alt}>
        <SectionTitle title={title} />
        {grid}
      </Band>
    );
  return (
    <section className={`section ${styles.featuresDark}`}>
      <div className="container">
        <SectionTitle title={title} />
        {grid}
      </div>
    </section>
  );
}

export function About({ heading, paragraphs, stats, alt }: Props<"about">) {
  return (
    <Band alt={alt} prose>
      {heading && <h2>{heading}</h2>}
      <Paragraphs items={paragraphs} />
      {stats.length > 0 && (
        <dl className={styles.stats}>
          {stats.map((s) => (
            <div key={s.label}>
              <dt>{s.label}</dt>
              <dd>{s.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </Band>
  );
}

export function Hiring({ heading, intro, positions, apply, alt }: Props<"hiring">) {
  return (
    <Band alt={alt} prose>
      {heading && <h2>{heading}</h2>}
      <Paragraphs items={intro} />
      {positions.length > 0 && (
        // role="list": Safari drops list semantics from a list-style: none list.
        <ul className={styles.positions} role="list">
          {positions.map((p) => (
            <li key={p.title}>
              <IconCard icon={<BadgeIcon />} title={p.title} as="h3">
                {p.schedule && <p className="muted">{p.schedule}</p>}
                {p.text && (
                  <p>
                    <RichText text={p.text} />
                  </p>
                )}
              </IconCard>
            </li>
          ))}
        </ul>
      )}
      <Paragraphs items={apply} />
    </Band>
  );
}

export function Text({ heading, paragraphs, note, actions, alt }: Props<"text">) {
  return (
    <Band alt={alt} prose>
      {heading && <h2>{heading}</h2>}
      <Paragraphs items={paragraphs} />
      {note && (
        <p className="muted">
          <RichText text={note} />
        </p>
      )}
      {actions.length > 0 && (
        <p className={styles.actions}>
          {actions.map((a) => (
            <ContentLinkView key={a.label} link={a} />
          ))}
        </p>
      )}
    </Band>
  );
}

export function Contact({ heading, text, addressTitle, hoursTitle, alt }: Props<"contact">) {
  const { line1, city, state, postalCode } = BRAND.address;
  return (
    <Band alt={alt} prose>
      {heading && <h2>{heading}</h2>}
      {text && (
        <p>
          <RichText text={text} />
        </p>
      )}
      {addressTitle && (
        <>
          <h2>{addressTitle}</h2>
          <address className={styles.address}>
            {line1}
            <br />
            {`${city}, ${state} ${postalCode}`}
          </address>
        </>
      )}
      <h2>{hoursTitle}</h2>
      <ul>
        {STORE_HOURS.map((h) => (
          <li key={h.days}>
            <strong>{h.days}:</strong> {h.hours}
          </li>
        ))}
      </ul>
    </Band>
  );
}

export function StoreInfo({ shopsTitle, hoursTitle, alt }: Props<"storeInfo">) {
  return (
    <section className={`section ${alt ? "section--alt" : ""}`}>
      <div className={`container ${styles.twoCol}`}>
        <div>
          <h2>{shopsTitle}</h2>
          <ul className={styles.shops}>
            {BRAND.shops.map((s) => (
              <li key={s.label}>
                <ContentLinkView link={{ label: s.label, to: s.href }} />
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h2>
            <ClockIcon /> {hoursTitle}
          </h2>
          <dl className={styles.hoursCard}>
            {STORE_HOURS.map((h) => (
              <div key={h.days}>
                <dt>{h.days}</dt>
                <dd>{h.hours}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}

export function MapSection({ heading, locations, alt }: Props<"map">) {
  const { line1, city, state, postalCode } = BRAND.address;
  const places = locations ?? [{ name: BRAND.name, address: `${line1}, ${city}, ${state} ${postalCode}` }];
  return (
    <Band alt={alt}>
      <SectionTitle title={heading} />
      <ul className={styles.mapCards}>
        {places.map((p) => (
          <li key={p.name} className={styles.mapCard}>
            {mapsEmbedKey && (
              // The referrer lets Google check the key's allowed sites.
              <iframe
                className={styles.map}
                title={`Map of ${p.name}`}
                src={mapEmbedUrl(mapsEmbedKey, p.address)}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            )}
            <h3>{p.name}</h3>
            <address className={styles.address}>{p.address}</address>
            <a href={directionsUrl(p.address)} target="_blank" rel="noopener noreferrer">
              Get directions
            </a>
          </li>
        ))}
      </ul>
    </Band>
  );
}

export function Departments({ title, all, alt }: Props<"departments">) {
  return (
    <Band alt={alt}>
      <SectionTitle title={title} />
      <DepartmentTiles includeAll={all} />
    </Band>
  );
}

export function Products({ title, subtitle, department, count, action, alt }: Props<"products">) {
  const popular = useProducts(popularProductsQuery({ count, department }));
  return (
    <Band alt={alt}>
      <SectionTitle title={title} subtitle={subtitle} />
      {popular.data ? (
        <ProductGrid products={popular.data.items} preview={isSnapshot(popular.data)} />
      ) : (
        <ProductGridSkeleton count={Math.min(count, 4)} />
      )}
      {action && (
        <div className={styles.after}>
          <ContentLinkView link={action} />
        </div>
      )}
    </Band>
  );
}

export function Brands({ title, exclude, action, alt }: Props<"brands">) {
  const { data: facets } = useFacets();
  return (
    <Band alt={alt}>
      <SectionTitle title={title} />
      <div className={styles.brandStrip}>
        {facets?.brands
          .filter((b) => !exclude.some((own) => b.name.startsWith(own)))
          .map((b) => (
            <Link key={b.slug} to={`/brands/${b.slug}`}>
              {b.name}
            </Link>
          ))}
      </div>
      {action && (
        <div className={styles.after}>
          <ContentLinkView link={action} />
        </div>
      )}
    </Band>
  );
}

function SectionView({ section: s }: { section: Section }) {
  switch (s.type) {
    case "hero":
      return <Hero {...s} />;
    case "pageHeader":
      return <PageHeader {...s} />;
    case "stripe":
      return <Stripe {...s} />;
    case "features":
      return <Features {...s} />;
    case "about":
      return <About {...s} />;
    case "hiring":
      return <Hiring {...s} />;
    case "text":
      return <Text {...s} />;
    case "contact":
      return <Contact {...s} />;
    case "storeInfo":
      return <StoreInfo {...s} />;
    case "map":
      return <MapSection {...s} />;
    case "departments":
      return <Departments {...s} />;
    case "products":
      return <Products {...s} />;
    case "brands":
      return <Brands {...s} />;
  }
}

/** A page's sections in order, leaving out any whose feature is off. */
export function Sections({ sections }: { sections: Section[] }) {
  return sections.filter(shown).map((s, i) => <SectionView key={`${i}-${s.type}`} section={s} />);
}
